import { parseWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { MAP_BUNDLE_NAMES } from 'cadenza/workbook/model/attribute-bundle-name';
import type { Workbook } from 'cadenza/workbook/workbook';
import type { WorkbookIdAndHash } from 'cadenza/workbook/api/workbook-id-and-hash';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import { navigateWithinWorkbook } from 'cadenza/utils/confirm-navigation-util';
import type { DelegateEvent } from 'cadenza/utils/event-util';
import { on } from 'cadenza/utils/event-util';

let workbook: Workbook | null; // Cached workbook

/** @deprecated Use actions and selectors from `workbook.state.js` instead */
export function getWorkbook (): Workbook {
  const workbookOptional = getWorkbookOptional();
  assertNonNullable(workbookOptional);
  return workbookOptional;
}

/**
 * We use the store for report printing where we don't use d-workbook.
 *
 * @return - The Workbook instance if present.
 */
export function getWorkbookOptional () {
  if (!workbook) {
    workbook = window.document.querySelector<Workbook>('d-workbook');
  }
  return workbook;
}

/**
 * Check whether or not we're currently in the workbook world.
 *
 * @return True, if the current window is part of the workbook world.
 *    False, if the current window is part of the classic world.
 */
export function isWorkbook (): boolean {
  return !!parseWorkbookUrl(location.href);
}

export function updateWorkbookUrl (workbookIdAndHash: WorkbookIdAndHash, push = false) {
  const { id, hash } = workbookIdAndHash;
  const newUrl = location.href.replace(/\/workbooks\/[^/]+/, `/workbooks/${id},hash=${hash}`);
  if (push) {
    history.pushState(null, '', newUrl);
  } else {
    history.replaceState(null, '', newUrl);
  }
}

export function isDataViewValidForMapAnimation (dataView: DataViewDto) {
  const geometryBundleName = MAP_BUNDLE_NAMES.GEOMETRY;
  if (dataView.attributes[geometryBundleName] === undefined) {
    return false;
  }
  const noOfAttributes = dataView.attributes[geometryBundleName].length;
  const minAttributes = dataView.attributeBundleDefinitions[geometryBundleName].minNumberOfAttributes;
  return !minAttributes || noOfAttributes >= minAttributes;
}

/**
 * Global listener for workbookIdAndHash links that includes 'embeddingTargetId'
 * query parameter in the navigated URL, if it is present in the current URL.
 */
export function keepEmbeddingTargetWhenFollowingLinks () {
  return on(document, 'click', (event: DelegateEvent) => {
    event.preventDefault();
    const linkUrl = (event.delegateTarget as HTMLAnchorElement).href;
    navigateWithinWorkbook(linkUrl);
  }, { delegate: 'a[href*="/workbooks"][href*=",hash="]' });
}
